import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['rowCheckbox', 'selectAll', 'combinedIds', 'filterItem'];

  #selectedIds = new Set();
  #sortDirections = {};

  // Re-applies the in-memory selection after a filter change swaps the frame.
  restore() {
    this.#sortDirections = {};
    this.rowCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = this.#selectedIds.has(checkbox.value);
    });
    this.#sync();
  }

  toggleRow(event) {
    const { value, checked } = event.target;

    if (checked) {
      this.#selectedIds.add(value);
    } else {
      this.#selectedIds.delete(value);
    }
    this.#sync();
  }

  toggleAll(event) {
    this.rowCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = event.target.checked;

      if (checkbox.checked) {
        this.#selectedIds.add(checkbox.value);
      } else {
        this.#selectedIds.delete(checkbox.value);
      }
    });
    this.#sync();
  }

  clearSelections() {
    this.#selectedIds.clear();
    this.rowCheckboxTargets.forEach((checkbox) => (checkbox.checked = false));
    this.#sync();
  }

  prepareSubmit(event) {
    if (this.#selectedIds.size === 0) {
      event.preventDefault();
      return;
    }

    this.combinedIdsTarget.value = [...this.#selectedIds].map((id) => `swatch-${id}`).join(',');
  }

  toggleFilter(event) {
    event.preventDefault();
    event.stopPropagation();

    const item = event.currentTarget.closest('.filter-list__item');
    const wasOpen = item.classList.contains('filter-list__item--open');

    this.#closeAllFilters();
    item.classList.toggle('filter-list__item--open', !wasOpen);
  }

  closeFilters(event) {
    if (this.filterItemTargets.some((item) => item.contains(event.target))) {
      return;
    }

    this.#closeAllFilters();
  }

  sort(event) {
    event.preventDefault();

    const link = event.currentTarget;
    const th = link.closest('.data-table__th');
    const table = th.closest('table');
    const { column } = event.params;

    const ascending = !(this.#sortDirections[column] ?? false);
    this.#sortDirections[column] = ascending;

    const rows = [...table.tBodies[0].querySelectorAll('.data-table__tr')].sort((a, b) =>
      this.#compareCells(a, b, th.cellIndex)
    );
    if (!ascending) {
      rows.reverse();
    }
    rows.forEach((row) => table.tBodies[0].appendChild(row));

    table
      .querySelectorAll('.data-table__th .data-table__sortable-icon')
      .forEach((icon) => icon.remove());
    const chevron = ascending ? 'fa-chevron-up' : 'fa-chevron-down';
    link.insertAdjacentHTML(
      'beforeend',
      `<i class="fa-regular ${chevron} fa-sm data-table__sortable-icon" aria-hidden="true"></i>`
    );
  }

  #compareCells(rowA, rowB, index) {
    const valueA = rowA.cells[index].textContent.trim();
    const valueB = rowB.cells[index].textContent.trim();
    const numberA = parseFloat(valueA.replace(/[^0-9.-]+/g, ''));
    const numberB = parseFloat(valueB.replace(/[^0-9.-]+/g, ''));

    if (!isNaN(numberA) && !isNaN(numberB)) {
      return numberA - numberB;
    }

    return valueA.localeCompare(valueB);
  }

  #closeAllFilters() {
    this.filterItemTargets.forEach((item) => item.classList.remove('filter-list__item--open'));
  }

  #sync() {
    const checkboxes = this.rowCheckboxTargets;
    this.selectAllTarget.checked =
      checkboxes.length > 0 && checkboxes.every((checkbox) => checkbox.checked);

    const count = this.#selectedIds.size;
    const buttons = [...this.element.elements];
    const submitButton = buttons.find(({ type }) => type === 'submit');
    const template = submitButton.dataset.labelTemplate;
    submitButton.textContent = template.replace(/NUM/, count > 0 ? count : '');
    submitButton.disabled = count === 0;
    buttons.find(({ type }) => type === 'reset').disabled = count === 0;
  }
}
